import { EventTypes, useEditor } from '@candulabs/craft';
import { Button } from 'canoe';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { TooltipPlacement } from 'canoe/components/Tooltip/Tooltip';
import { useActiveTooltip, useTooltipFlow } from 'craft/components/Tooltip';
import { FloatingMenuTab, TabErrorSeverity } from 'craft/containers/FloatingMenu';
import { checkInvalidSelector } from 'craft/containers/Toolbox/tabs/Placements/utils';
import { FLOATING_MENU_CUSTOMIZATION } from 'craft/containers/TooltipFloatingMenu/FloatingMenuCustomization';
import { useMultiNodes, useTooltipPlacement } from 'craft/hooks';
import { setToolboxTab, useToolboxState } from 'craft/state';
import { ToolboxTabs } from 'craft/types';
import { TooltipType } from 'craft/types/TooltipType';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

interface Props {
  contentType: TooltipType;
  id: string;
  nodeId: string;
  onContextClick: (dom: HTMLButtonElement | null) => void;
  tooltipPlacement: TooltipPlacement;
  isContextOpen?: string;
}

export const TooltipTab = ({
  id,
  nodeId,
  onContextClick,
  tooltipPlacement,
  contentType,
  isContextOpen,
}: Props) => {
  const { actions } = useEditor();
  const dispatch = useDispatch();
  const { placement } = useTooltipPlacement(nodeId);
  const { isTooltipConnected, getPreviousTooltipId } = useTooltipFlow(contentType);
  const { forceOpenToolbox } = useToolboxState();

  const disconnected = useMemo(() => !isTooltipConnected(nodeId), [isTooltipConnected, nodeId]);

  const { invalidSelector, querySelector } = checkInvalidSelector(placement?.selector);
  const notVisibleOnPage = useMemo(() => !invalidSelector && !querySelector, [
    invalidSelector,
    querySelector,
  ]);

  const { hotspotName } = useMultiNodes([nodeId], (node) => ({
    hotspotName: node?.data.custom?.displayName || node?.data.displayName || 'Hotspot',
  }));

  const onSelectHotspot = useCallback(
    (overrideNodeId?: string) => {
      const targetId = overrideNodeId || nodeId;

      // Scrolling the hotspot into view before expanding it gives us a more natural tooltip position
      const targetEl = getSelectorFromIframe(placement?.selector);
      targetEl?.scrollIntoView({ block: 'center' });

      actions.setEventNodes(EventTypes.selected, [targetId]);

      if (invalidSelector || notVisibleOnPage) {
        // Open the placements tab if there's an error with the selector.
        dispatch(setToolboxTab(ToolboxTabs.Placements));
        // when automatically switching to placements tab, we want to show the toolbox
        forceOpenToolbox();
      } else if (disconnected) {
        // Open the interactions tab if the step is disconnected but the selector is fine.
        dispatch(setToolboxTab(ToolboxTabs.Interactions));
      }
    },
    [nodeId, invalidSelector, notVisibleOnPage, disconnected],
  );

  const activeHotspot = useActiveTooltip();
  const isTabActive = activeHotspot?.id === nodeId;

  const TooltipIcon = FLOATING_MENU_CUSTOMIZATION[contentType].Icon;

  const selectPreviousTooltipAndOpenInteractions = useCallback(
    (e) => {
      e.stopPropagation();

      onSelectHotspot(getPreviousTooltipId(nodeId));
      dispatch(setToolboxTab(ToolboxTabs.Interactions));
    },
    [nodeId],
  );

  const errors = useMemo(() => {
    const errorsList = [];
    if (notVisibleOnPage) {
      errorsList.push({
        severity: TabErrorSeverity.Warning,
        content: 'Selector is not visible on this page.',
      });
    }
    if (invalidSelector) {
      errorsList.push({
        severity: TabErrorSeverity.Danger,
        content: (
          <>
            Selector is not valid. Use{' '}
            <Button
              inline
              appearance="text"
              variant="white"
              size="xsmall"
              showUnderlined
              onClick={() => onSelectHotspot()}
            >
              placements
            </Button>{' '}
            to choose a valid selector.
          </>
        ),
      });
    }
    if (disconnected) {
      errorsList.push({
        severity: TabErrorSeverity.Danger,
        content: (
          <>
            Please check that the previous step is connected. Select a CTA and use{' '}
            <Button
              inline
              appearance="text"
              variant="white"
              size="xsmall"
              showUnderlined
              onClick={selectPreviousTooltipAndOpenInteractions}
            >
              interactions
            </Button>{' '}
            to connect steps.
          </>
        ),
      });
    }

    return errorsList;
  }, [
    notVisibleOnPage,
    invalidSelector,
    disconnected,
    onSelectHotspot,
    selectPreviousTooltipAndOpenInteractions,
  ]);

  return (
    <FloatingMenuTab
      id={id}
      nodeId={nodeId}
      nodeName={hotspotName}
      isActive={isTabActive}
      tooltipPlacement={tooltipPlacement}
      onSelect={() => onSelectHotspot()}
      onContextClick={onContextClick}
      tabIcon={TooltipIcon}
      errors={errors}
      isContextOpen={!!isContextOpen && isContextOpen === activeHotspot?.id}
    />
  );
};
